import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Popover } from '@scholastic/mockingjay';
import InfoIcon from '@data/assets/bold-circle-info.svg';
import { Table } from '@data/components/basic/Table/Table';
import type { TableColumn, TableRow } from '@data/components/basic/Table/types';
import {
  ProficiencyLegend,
  renderLevelCell,
  renderMostStudentsCell,
} from '@data/components/basic/ProficiencySwatch/ProficiencySwatch';
import { Drilldown } from '@data/components/report/ReportFilterBar/ReportFilterBar';
import { useAllClassSkills } from '@data/hooks/use-report-tables/use-all-class-skills/use-all-class-skills';
import { useTableControls } from '@data/hooks/use-report-tables/use-table-controls/use-table-controls';
import { useStudentSkills } from '@data/hooks/use-report-tables/use-student-skills/use-student-skills';
import { useResetTableOnFilterChange } from '@data/hooks/use-report-tables/use-reset-table-on-filter-change/use-reset-table-on-filter-change';
import { useReportOutletContext } from '@data/pages/ReportLayout/ReportLayout';
import { exportTableToCSV } from '@data/utils/export-table-to-csv';
import { filterTableData } from '@data/utils/filter-table-data';
import formatDate from '@data/utils/format-date';
import type { ColumnRule } from '@data/utils/resolve-columns';
import {
  buildRowTree,
  clearChildColumns,
  filterRowTree,
  flattenRowTree,
  sortRowTree,
} from '@data/utils/row-tree/row-tree';
import {
  MOST_STUDENTS_KEY,
  withMostStudentsColumn,
} from '@data/utils/proficiency/proficiency';

const SKILL_KEY = 'SKILL';
const AREA_KEY = 'AREA';
const SCORE_KEY = 'SCORE';
const BAND_KEY = 'BAND';
/** The skill's depth in the tree; the nesting already shows it. */
const LEVEL_KEY = 'LEVEL';

/** `NODE_ID` and `PARENT_ID` are id columns, kept for nesting but not shown. */
const SKILLS_TABLE_COLUMNS: ColumnRule[] = [
  { key: SKILL_KEY },
  { key: AREA_KEY },
  { key: SCORE_KEY },
  { key: 'LEARNING' },
  { key: 'PRACTICING' },
  { key: 'PROFICIENT' },
  { key: 'NO_DATA' },
];

/** The service sends names in lower case; the design starts them capitalised. */
function capitalizeColumns(row: TableRow, indices: number[]): TableRow {
  return row.map((value, index) =>
    indices.includes(index) && typeof value === 'string'
      ? value.charAt(0).toUpperCase() + value.slice(1)
      : value
  );
}

function withoutColumn(
  { visibleColumns, visibleRows }: ReturnType<typeof filterTableData>,
  key: string
) {
  const index = visibleColumns.findIndex((col) => col.key === key);
  if (index < 0) return { visibleColumns, visibleRows };
  return {
    visibleColumns: visibleColumns.filter((_, i) => i !== index),
    visibleRows: visibleRows.map((row) => row.filter((_, i) => i !== index)),
  };
}

function renderSkillCell(
  value: TableRow[number],
  column: TableColumn,
  _row: TableRow,
  depth: number
) {
  if (column.key === MOST_STUDENTS_KEY) return renderMostStudentsCell(value);
  if (column.key === BAND_KEY) return renderLevelCell(value);
  // A skill's area is its group's, so it is left blank, not marked missing.
  if (column.key === AREA_KEY && depth > 0) return '';
  return undefined;
}

/**
 * Each skill group's average and how many students sit at each proficiency
 * level, with the group's skills nested beneath it. For one student, their
 * score and level on each instead.
 */
export function SkillPerformanceTable() {
  const { drilldown, productId: edApp } = useReportOutletContext();
  const [searchParams] = useSearchParams();
  const classIds = searchParams.getAll('classId');
  const studentIds = searchParams.getAll('studentId');
  const forStudent = drilldown === Drilldown.Student;

  const { tableProps, searchTerms, reset } = useTableControls({
    defaultSortColumn: SCORE_KEY,
    defaultSortDirection: 'asc',
  });

  useResetTableOnFilterChange(
    drilldown,
    classIds.join(','),
    studentIds.join(','),
    reset
  );

  // Every skill comes in one pass, so search and sort happen here.
  const classSkills = useAllClassSkills({ enabled: !forStudent });
  const studentSkills = useStudentSkills({ enabled: forStudent });
  const { data, loading, error } = forStudent ? studentSkills : classSkills;

  const { columns, tree } = useMemo(() => {
    // A student's columns are the service's own; there are no level counts.
    const { visibleColumns, visibleRows } = forStudent
      ? withoutColumn(filterTableData(data), LEVEL_KEY)
      : filterTableData(data, SKILLS_TABLE_COLUMNS, edApp);
    const nameIndices = [SKILL_KEY, AREA_KEY].map((key) =>
      visibleColumns.findIndex((col) => col.key === key)
    );
    const capitalized = visibleRows.map((row) =>
      capitalizeColumns(row, nameIndices)
    );
    const table = forStudent
      ? { columns: visibleColumns, rows: capitalized }
      : withMostStudentsColumn(visibleColumns, capitalized, SCORE_KEY);
    return {
      columns: table.columns,
      // A skill's area is its group's, so only the group shows it.
      tree: clearChildColumns(
        buildRowTree(table.columns, table.rows),
        table.columns,
        [AREA_KEY]
      ),
    };
  }, [data, edApp, forStudent]);

  const { sortColumn, sortDirection } = tableProps;
  const visibleTree = useMemo(
    () =>
      sortRowTree(
        filterRowTree(tree, columns, searchTerms, SKILL_KEY),
        columns,
        {
          key: sortColumn ?? SCORE_KEY,
          direction: sortDirection ?? 'asc',
          tieBreakKey: SKILL_KEY,
        }
      ),
    [tree, columns, searchTerms, sortColumn, sortDirection]
  );

  // Every searched group then its skills, each skill naming its group; the
  // most-students column carries the level's name, not its colour.
  const handleExportToCSV = useCallback(async () => {
    const exported = flattenRowTree(visibleTree, columns, SKILL_KEY, {
      key: 'PARENT_SKILL',
      label: 'Parent Skill',
    });
    exportTableToCSV(
      exported.columns,
      exported.rows,
      `skill-performance-by-skill${
        forStudent ? '_student' : ''
      }_${edApp}_${formatDate(new Date())}.csv`
    );
  }, [columns, visibleTree, edApp, forStudent]);

  return (
    <section
      aria-labelledby="skill-performance-heading"
      className="flex flex-col gap-4"
    >
      <div className="flex items-center gap-2">
        <h3
          id="skill-performance-heading"
          className="text-lg font-bold text-neutral-1300 tablet:text-xl"
        >
          Performance by Skill
        </h3>
        {data?.toolTips && (
          <Popover
            trigger={
              <button
                type="button"
                aria-label="Performance by Skill information"
                className="flex items-center"
              >
                <InfoIcon aria-hidden="true" className="h-5 w-5" />
              </button>
            }
          >
            {data.toolTips}
          </Popover>
        )}
      </div>
      <ProficiencyLegend />
      <Table
        {...tableProps}
        // No pagination: every skill is on the one page.
        page={0}
        loading={loading}
        columns={columns}
        rows={visibleTree.rows}
        childRows={visibleTree.childRows}
        totalCount={visibleTree.rows.length}
        renderCell={renderSkillCell}
        leftAlignedColumns={[AREA_KEY, BAND_KEY, MOST_STUDENTS_KEY]}
        onExportToCSV={handleExportToCSV}
        scrollRegionLabel="Performance by Skill table, scrollable"
        error={error}
      />
    </section>
  );
}
